import type { Product, Variant } from "@shared/types";

// Typed against the WebStatus union (non-nullable) so a typo is caught at compile
// time rather than silently never matching.
const VISIBLE_FOR_SALE: NonNullable<Variant["webStatus"]> = "Visible for Sale";
const OUT_OF_STOCK: NonNullable<Variant["webStatus"]> = "Out of Stock";
const VISIBLE_BUT_NOT_FOR_SALE: NonNullable<Variant["webStatus"]> = "Visible but not for Sale";

// Statuses that make a variant mappable in the sync modal.
//
// "Out of Stock" is included by product decision: the remap modal is a
// roaster-facing workflow, not a customer-facing shopping experience. The OOS
// flag controls purchase availability for customers, not whether a roaster may
// manage their own SKU relationships — a roaster may be remapping ahead of a
// restock, or maintaining connections while a SKU is temporarily out of stock.
// Excluding OOS variants stranded them: they were absent from the modal with no
// way to be re-mapped.
//
// "Visible but not for Sale" follows for the same reason and is presented to
// roasters as "Out of Stock" — same label, same badge, same filter chip. Were it
// excluded, two rows a roaster cannot tell apart would behave differently, with
// nothing in the UI to explain why.
const MAPPABLE_WEB_STATUSES: ReadonlySet<NonNullable<Variant["webStatus"]>> = new Set([
  VISIBLE_FOR_SALE,
  OUT_OF_STOCK,
  VISIBLE_BUT_NOT_FOR_SALE,
]);

const isMappableWebStatus = (webStatus: Variant["webStatus"]): boolean => webStatus !== undefined && MAPPABLE_WEB_STATUSES.has(webStatus);

// A variant counts as mappable when its own webStatus is mappable OR — for
// cross-border SKUs whose real status lives per ship-to channel — any of its
// shipToInfo entries is mappable. The exported name is retained for compatibility
// with existing callers; its contract is the broader mapping-specific rule.
export function isVariantVisibleForSale(variant: Variant): boolean {
  if (isMappableWebStatus(variant.webStatus)) {
    return true;
  }
  return (variant.shipToInfo ?? []).some((info) => isMappableWebStatus(info.webStatus));
}

export function filterEligibleProducts(products: Product[]): Product[] {
  return products.filter((product) => {
    const variants = product.variants ?? [];

    // A product is eligible when at least one variant can be mapped. `.some`
    // returns false for an empty variants array, so products with no variants
    // and products whose variants all have excluded statuses remain hidden.
    return variants.some(isVariantVisibleForSale);
  });
}

export function filterProductsBySearch(products: Product[], query: string): Product[] {
  const trimmed = query.trim();
  if (trimmed === "") {
    return products;
  }
  const lower = trimmed.toLowerCase();
  return products.filter((product) => (product.title ?? "").toLowerCase().includes(lower));
}
